// features/subscription/UserSubscriptions.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const UserSubscriptions = () => {
  const { t } = useTranslation();
  const { 
    userSubscriptions, 
    cancelSubscription, 
    renewSubscription, 
    loading, 
    error 
  } = useSubscription();
  
  const [cancellingId, setCancellingId] = useState(null);
  const [renewingId, setRenewingId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);

  const handleCancelClick = (subscription) => {
    setSubscriptionToCancel(subscription);
    setIsConfirmModalOpen(true);
    setActionError(null);
    setActionSuccess(null);
  };

  const confirmCancel = async () => {
    if (!subscriptionToCancel) return;
    
    try {
      setCancellingId(subscriptionToCancel.id);
      setActionError(null);
      setActionSuccess(null);
      setIsConfirmModalOpen(false);
      
      await cancelSubscription(subscriptionToCancel.id);
      
      setActionSuccess(t('subscription.cancelSuccess'));
    } catch (err) {
      setActionError(err.message || t('subscription.cancelError'));
    } finally {
      setCancellingId(null);
    }
  };

  const handleRenew = async (subscriptionId) => {
    try {
      setRenewingId(subscriptionId);
      setActionError(null);
      setActionSuccess(null);
      
      await renewSubscription(subscriptionId);
      
      setActionSuccess(t('subscription.renewSuccess'));
    } catch (err) {
      setActionError(err.message || t('subscription.renewError'));
    } finally {
      setRenewingId(null);
    }
  };

  if (loading && userSubscriptions.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-label={t('common.loading')}></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center dark:text-white">{t('subscription.yourSubscriptions')}</h1>
      
      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {actionError && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {actionError}
        </div>
      )}
      
      {actionSuccess && (
        <div className="bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-200 px-4 py-3 rounded mb-4">
          {actionSuccess}
        </div>
      )}
      
      {userSubscriptions.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">{t('subscription.noSubscriptions')}</p>
          <Link 
            to="/subscription/plans" 
            className="inline-block bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded dark:bg-primary-dark dark:hover:bg-primary-dark"
          >
            {t('subscription.explorePlans')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {userSubscriptions.map((subscription) => {
            const isActive = new Date(subscription.end_date) > new Date();
            const isCancelled = subscription.status === 'cancelled';
            
            return (
              <div 
                key={subscription.id} 
                className="border rounded-lg p-6 shadow-sm dark:bg-gray-800 dark:border-gray-700"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <h2 className="text-xl font-bold dark:text-white">{subscription.plan_name}</h2>
                      <span className={`ml-3 px-3 py-1 text-xs font-bold rounded-full ${
                        isActive && !isCancelled 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : isCancelled 
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {isActive && !isCancelled 
                          ? t('subscription.statusActive') 
                          : isCancelled 
                            ? t('subscription.statusCancelled') 
                            : t('subscription.statusExpired')}
                      </span>
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-300 mb-4">
                      <p>{t('subscription.price')}: ${parseFloat(subscription.price || 0).toFixed(2)} / {t('subscription.month')}</p>
                      <p>{t('subscription.startDate')}: {new Date(subscription.start_date).toLocaleDateString()}</p>
                      <p>{t('subscription.endDate')}: {new Date(subscription.end_date).toLocaleDateString()}</p>
                      <p>{t('subscription.autoRenew')}: {subscription.auto_renew ? t('common.yes') : t('common.no')}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {isActive && !isCancelled && (
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded dark:bg-red-600 dark:hover:bg-red-700"
                        onClick={() => handleCancelClick(subscription)}
                        disabled={cancellingId === subscription.id}
                      >
                        {cancellingId === subscription.id ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('common.processing')}
                          </span>
                        ) : t('subscription.cancel')}
                      </button>
                    )}
                    
                    {!isActive && !isCancelled && (
                      <button
                        className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded dark:bg-primary-dark dark:hover:bg-primary-dark"
                        onClick={() => handleRenew(subscription.id)}
                        disabled={renewingId === subscription.id}
                      >
                        {renewingId === subscription.id ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('common.processing')}
                          </span>
                        ) : t('subscription.renew')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal */}
      <Transition appear show={isConfirmModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsConfirmModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 dark:bg-black/50" aria-hidden="true" />
          </Transition.Child>
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transform transition-all">
                <Dialog.Title className="text-xl font-bold mb-4 dark:text-white">
                  {t('subscription.confirmCancellation')}
                </Dialog.Title>
                
                <div className="mb-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {t('subscription.confirmCancellationMessage', { 
                      planName: subscriptionToCancel?.plan_name
                    })}
                  </p>
                  
                  {subscriptionToCancel && (
                    <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                      <div className="flex justify-between mb-2">
                        <span className="font-medium dark:text-gray-300">{t('subscription.planName')}</span>
                        <span className="dark:text-gray-300">{subscriptionToCancel.plan_name}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium dark:text-gray-300">{t('subscription.endDate')}</span>
                        <span className="dark:text-gray-300">{new Date(subscriptionToCancel.end_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-300">{t('subscription.price')}</span>
                        <span className="dark:text-gray-300">
                          ${parseFloat(subscriptionToCancel.price || 0).toFixed(2)} / {t('subscription.month')}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-gray-700 dark:text-gray-300 mt-4">
                    {t('subscription.cancellationWarning')}
                  </p>
                </div>
                
                <div className="flex space-x-4">
                  <button
                    className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
                    onClick={() => setIsConfirmModalOpen(false)}
                  >
                    {t('common.keepSubscription')}
                  </button>
                  <button
                    className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded dark:bg-red-600 dark:hover:bg-red-700"
                    onClick={confirmCancel}
                  >
                    {t('common.confirmCancel')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default UserSubscriptions;
